import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styledComponent from '../../utils/styledComponent';
import theme from '../../theme';
import Loader from '../../components/atoms/Loader';
import MultiSelect from '../../components/atoms/forms/MultiSelect';
import { GetLocationRoutes } from '../../libs/GraphQL';
import { STATUS_IDS } from '../../libs/stopStatus';
import useDriverActionStatuses from '../../hooks/useDriverActionStatuses';
import { getCurrentUserEmail } from '../../utils/actorEmail';
import { pluralize } from '../../utils/displayFormat';
import { formatFullDate } from '../../utils/datetime';
import { PRETTY_TOAST_OPTS as toastOpts } from '../../utils/toast';
import { applyClientFilters, formatDateForApi } from './utils';
import { getStopRowKey, groupStopsByRoute } from './RouteOperations/utils';
import DispatchRouteCard from './DispatchTab/DispatchRouteCard';
import DispatchReasonModal from './DispatchTab/DispatchReasonModal';
import {
  isDispatchSelectable,
  isUnreachableStatus,
} from './DispatchTab/dispatchStops';
import {
  DISPATCH_REASON,
  dispatchSubReasonLabel,
} from './DispatchTab/dispatchReasons';
import { markStopsUnreachable } from './DispatchTab/markStopsUnreachable';

const StyledDiv = styledComponent('div', {
  '& .dsp-toolbar': {
    position: 'sticky',
    top: 0,
    zIndex: theme.zIndices.stickyToolbar,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing.fieldGap,
    padding: theme.spacing.bodyPadding,
    marginBottom: theme.spacing.sectionGap,
    background: theme.colors.bgCard,
    border: theme.borders.card,
    borderRadius: theme.radii.card,
    boxShadow: theme.shadows.card,
  },
  '& .dsp-title': {
    fontSize: theme.typography.sectionTitle.fontSize,
    fontWeight: theme.typography.sectionTitle.fontWeight,
    color: theme.colors.textDark,
  },
  '& .dsp-subtitle': {
    fontSize: theme.typography.label.fontSize,
    color: theme.colors.textMuted,
    marginTop: theme.spacing.labelGap,
  },
  '& .dsp-heading': {
    flex: '1 1 auto',
    minWidth: 0,
  },
  '& .dsp-route-filter': {
    display: 'flex',
    flexDirection: 'column',
  },
  '& .dsp-route-filter-label': {
    ...theme.typography.label,
    color: theme.colors.text,
    marginBottom: theme.spacing.inputLabelGap,
  },
  '& .dsp-error': {
    color: theme.colors.danger,
    background: theme.colors.dangerBg,
    borderRadius: theme.radii.input,
    padding: `${theme.spacing.gapLg} ${theme.spacing.gapXl}`,
    marginBottom: theme.spacing.sectionGap,
    fontSize: theme.typography.label.fontSize,
  },
  '& .dsp-routes': {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing.sectionGap,
    alignItems: 'start',
  },
  [`@media (min-width: ${theme.breakpoints.desktop})`]: {
    '& .dsp-routes': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  '& .dsp-routes > *': {
    marginBottom: 0,
    minWidth: 0,
  },
  '& .dsp-empty': {
    textAlign: 'center',
    padding: theme.spacing.emptyStatePadding,
    color: theme.colors.textMuted,
    fontStyle: 'italic',
    background: theme.colors.bgCard,
    borderRadius: theme.radii.card,
    border: theme.borders.card,
    boxShadow: theme.shadows.card,
  },
});

function DispatchTab({
  region,
  market,
  fcByLocationId,
  nameByLocationId,
  stockingDate,
  refetchKey,
  onLoadingChange,
  boardRefreshKey,
  onBoardRefreshLoadingChange,
  onBoardRefreshed,
}) {
  const stockingDateStr = useMemo(
    () => formatDateForApi(stockingDate),
    [stockingDate],
  );
  const [routes, setRoutes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshingRoutes, setIsRefreshingRoutes] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [markingRoute, setMarkingRoute] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [routeFilter, setRouteFilter] = useState([]);
  const requestIdRef = useRef(0);
  const prevBoardRefreshKeyRef = useRef(boardRefreshKey);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    let cancelled = false;
    const isStale = () => cancelled || requestIdRef.current !== requestId;

    const isRefresh = boardRefreshKey !== prevBoardRefreshKeyRef.current;
    prevBoardRefreshKeyRef.current = boardRefreshKey;
    if (isRefresh) {
      setIsRefreshingRoutes(true);
    } else {
      setIsLoading(true);
    }

    GetLocationRoutes({ stockingDate: stockingDateStr })
      .then((response) => {
        if (isStale()) return;
        setRoutes((response && response.routes) || []);
      })
      .catch((e) => {
        if (isStale()) return;
        console.error('DispatchTab routes fetch failed:', e);
        toast.error(
          `Failed fetching routes for ${stockingDateStr}. Please try again.`,
          toastOpts,
        );
        setRoutes([]);
      })
      .finally(() => {
        if (isStale()) return;
        setIsLoading(false);
        setIsRefreshingRoutes(false);
      });

    return () => {
      cancelled = true;
    };
  }, [stockingDateStr, refetchKey, boardRefreshKey]);

  useEffect(() => {
    onLoadingChange(isLoading);
    return () => {
      onLoadingChange(false);
    };
  }, [isLoading, onLoadingChange]);

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [stockingDateStr]);

  const marketValue = region ? region.value : undefined;
  const statusDates = useMemo(() => [stockingDateStr], [stockingDateStr]);
  const {
    statusesByDate,
    isLoading: isLoadingStatuses,
    error: statusesError,
    refresh: refreshStatuses,
  } = useDriverActionStatuses({
    dates: statusDates,
    routes,
    marketValue,
  });

  useEffect(() => {
    if (boardRefreshKey > 0) refreshStatuses();
    // `refresh` is identity-stable per the hook contract; only the key bump
    // should re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardRefreshKey]);

  const isBoardRefreshing = isLoadingStatuses || isRefreshingRoutes;
  useEffect(() => {
    onBoardRefreshLoadingChange(isBoardRefreshing);
    if (!isBoardRefreshing) onBoardRefreshed(new Date());
  }, [isBoardRefreshing, onBoardRefreshLoadingChange, onBoardRefreshed]);

  const enrichedRoutes = useMemo(() => {
    const statuses = statusesByDate[stockingDateStr];
    return routes.map((route) => ({
      ...route,
      fulfillmentCenter:
        fcByLocationId[route.id] || route.fulfillmentCenter || '',
      displayName: nameByLocationId[route.id] || route.id,
      derivedStatus: (statuses && statuses.get(route.id)) || {
        status: STATUS_IDS.NOT_STARTED,
      },
    }));
  }, [
    routes,
    fcByLocationId,
    nameByLocationId,
    statusesByDate,
    stockingDateStr,
  ]);

  const filteredRoutes = useMemo(
    () => applyClientFilters(enrichedRoutes, { region, market }),
    [enrichedRoutes, region, market],
  );
  const routeGroups = useMemo(
    () => groupStopsByRoute(filteredRoutes),
    [filteredRoutes],
  );
  const routeOptions = useMemo(
    () =>
      routeGroups.map(({ routeName }) => ({
        value: routeName,
        label: routeName,
      })),
    [routeGroups],
  );
  const activeRouteFilter = useMemo(() => {
    const available = new Set(routeOptions.map((o) => o.value));
    return routeFilter.filter((o) => available.has(o.value));
  }, [routeFilter, routeOptions]);
  const visibleRouteGroups = useMemo(() => {
    if (activeRouteFilter.length === 0) return routeGroups;
    const chosen = new Set(activeRouteFilter.map((o) => o.value));
    return routeGroups.filter(({ routeName }) => chosen.has(routeName));
  }, [routeGroups, activeRouteFilter]);
  // Selection spans every route Region/Market allow, not just the ones the
  // route filter shows, so narrowing the view never silently drops checked
  // stops; the modal's review step lists them all before confirming.
  const orderedStops = useMemo(
    () => routeGroups.reduce((acc, group) => acc.concat(group.stops), []),
    [routeGroups],
  );
  const visibleStops = useMemo(
    () =>
      visibleRouteGroups.reduce((acc, group) => acc.concat(group.stops), []),
    [visibleRouteGroups],
  );
  const selectableCount = visibleStops.filter(isDispatchSelectable).length;
  const unreachableCount = visibleStops.filter((stop) =>
    isUnreachableStatus(stop.derivedStatus.status),
  ).length;
  const selectedStops = useMemo(
    () =>
      orderedStops.filter(
        (stop) =>
          isDispatchSelectable(stop) && selectedKeys.has(getStopRowKey(stop)),
      ),
    [orderedStops, selectedKeys],
  );
  const stopsToMark = useMemo(
    () =>
      markingRoute === null
        ? []
        : selectedStops.filter((stop) => stop.route === markingRoute),
    [selectedStops, markingRoute],
  );

  const toggleStop = useCallback((stopKey, checked) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(stopKey);
      else next.delete(stopKey);
      return next;
    });
  }, []);

  const toggleMany = useCallback((stopKeys, checked) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      stopKeys.forEach((key) => {
        if (checked) next.add(key);
        else next.delete(key);
      });
      return next;
    });
  }, []);

  const handleConfirm = useCallback(
    async (reason) => {
      if (stopsToMark.length === 0 || isSubmitting) return;
      setIsSubmitting(true);
      try {
        const supervisorEmail =
          (await getCurrentUserEmail()) ||
          window.localStorage.getItem('userEmail');
        if (!supervisorEmail) {
          toast.error(
            'Could not determine your email — sign out and back in, then try again.',
            toastOpts,
          );
          return;
        }

        const { succeeded, failed } = await markStopsUnreachable({
          stops: stopsToMark,
          supervisorEmail,
          stockingDate: stockingDateStr,
          reason,
        });

        if (succeeded.length > 0) {
          toast.success(
            `Marked ${pluralize(succeeded.length, 'stop')} unreachable — ${
              DISPATCH_REASON.label
            } · ${dispatchSubReasonLabel(reason.subReason)}.`,
            toastOpts,
          );
        }

        if (failed.length > 0) {
          console.error('DispatchTab unreachable write failures:', failed);
          toast.error(
            `${pluralize(
              failed.length,
              'stop',
            )} could not be marked unreachable and remain selected so you can retry.`,
            toastOpts,
          );
        }

        setSelectedKeys((prev) => {
          const next = new Set(prev);
          succeeded.forEach((stop) => next.delete(getStopRowKey(stop)));
          return next;
        });
        refreshStatuses();
        setMarkingRoute(null);
      } finally {
        setIsSubmitting(false);
      }
    },
    [stopsToMark, isSubmitting, stockingDateStr, refreshStatuses],
  );

  const selectionDisabled =
    isLoadingStatuses || isSubmitting || Boolean(statusesError);
  const dateLabel = formatFullDate(stockingDate);

  return (
    <StyledDiv>
      <div className="dsp-toolbar" data-testid="dispatch-toolbar">
        <div className="dsp-heading">
          <div className="dsp-title">{`Dispatch Management · ${dateLabel}`}</div>
          <div className="dsp-subtitle" data-testid="dispatch-summary">
            {`${pluralize(
              visibleRouteGroups.length,
              'route',
            )} · ${selectableCount} of ${pluralize(
              visibleStops.length,
              'stop',
            )} can be marked · ${unreachableCount} already unreachable`}
          </div>
        </div>
        <div className="dsp-route-filter" data-testid="dispatch-route-filter">
          <div className="dsp-route-filter-label">Routes</div>
          <MultiSelect
            options={routeOptions}
            value={activeRouteFilter}
            onChange={(next) => setRouteFilter(next || [])}
            isDisabled={isLoading || routeOptions.length === 0}
            maxMenuHeight={400}
            width={theme.sizes.marketSelectWidth}
          />
        </div>
      </div>

      {statusesError && (
        <div className="dsp-error" data-testid="dispatch-statuses-error">
          Stop statuses could not be loaded, so selection is disabled. Refresh
          to try again.
        </div>
      )}

      {isLoading && <Loader />}
      {!isLoading && visibleRouteGroups.length === 0 && (
        <div className="dsp-empty" data-testid="dispatch-empty">
          {`No routes found for ${dateLabel} with the current filters.`}
        </div>
      )}
      {!isLoading && visibleRouteGroups.length > 0 && (
        <div className="dsp-routes" data-testid="dispatch-routes">
          {visibleRouteGroups.map(({ routeName, stops }) => (
            <DispatchRouteCard
              key={routeName}
              routeName={routeName}
              stops={stops}
              selectedKeys={selectedKeys}
              disabled={selectionDisabled}
              onToggleStop={toggleStop}
              onToggleMany={toggleMany}
              onMarkRoute={setMarkingRoute}
            />
          ))}
        </div>
      )}

      <DispatchReasonModal
        show={markingRoute !== null}
        selectedStops={stopsToMark}
        isSubmitting={isSubmitting}
        onConfirm={handleConfirm}
        onCancel={() => setMarkingRoute(null)}
      />
    </StyledDiv>
  );
}

DispatchTab.propTypes = {
  region: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  market: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  fcByLocationId: PropTypes.objectOf(PropTypes.string),
  nameByLocationId: PropTypes.objectOf(PropTypes.string),
  stockingDate: PropTypes.instanceOf(Date).isRequired,
  refetchKey: PropTypes.number,
  onLoadingChange: PropTypes.func,
  boardRefreshKey: PropTypes.number,
  onBoardRefreshLoadingChange: PropTypes.func,
  onBoardRefreshed: PropTypes.func,
};

DispatchTab.defaultProps = {
  region: null,
  market: [],
  fcByLocationId: {},
  nameByLocationId: {},
  refetchKey: 0,
  onLoadingChange: () => {},
  boardRefreshKey: 0,
  onBoardRefreshLoadingChange: () => {},
  onBoardRefreshed: () => {},
};

export default DispatchTab;
